import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listAccounts } from '../lib/api'
import {
  BG_PRESETS,
  useUiStore,
  type AiPrivacy,
  type BackgroundId,
  type Density,
  type Theme,
} from '../lib/uiStore'
import { Icon, type IconName } from './Icon'

const THEMES: { id: Theme; labelKey: string; icon: IconName }[] = [
  { id: 'light', labelKey: 'settings.themeLight', icon: 'light_mode' },
  { id: 'dark', labelKey: 'settings.themeDark', icon: 'dark_mode' },
  { id: 'system', labelKey: 'settings.themeSystem', icon: 'desktop_windows' },
]

const DENSITIES: { id: Density; labelKey: string }[] = [
  { id: 'compact', labelKey: 'settings.densityCompact' },
  { id: 'standard', labelKey: 'settings.densityStandard' },
  { id: 'comfortable', labelKey: 'settings.densityComfortable' },
]

const AI_MODES: { id: AiPrivacy; labelKey: string; noteKey: string }[] = [
  { id: 'disabled', labelKey: 'settings.aiDisabled', noteKey: 'settings.aiDisabledNote' },
  { id: 'local', labelKey: 'settings.aiLocal', noteKey: 'settings.aiLocalNote' },
  { id: 'private_7gc', labelKey: 'settings.aiPrivate', noteKey: 'settings.aiPrivateNote' },
  { id: 'external', labelKey: 'settings.aiExternal', noteKey: 'settings.aiExternalNote' },
]

const BGS = Object.keys(BG_PRESETS) as BackgroundId[]

const FALLBACK_COLORS = ['#ea4335', '#0078d4', '#00796b', '#6d28d9', '#c2410c']

type SettingsModalProps = {
  /** Inline form for drill-down section (no overlay). */
  embedded?: boolean
  forceOpen?: boolean
  onClose?: () => void
  /** Open full account customize panel (color/initial/label). */
  onCustomizeAccount?: (accountId: string) => void
  /** Jump to add-account flow (rail + / Dodaj). */
  onAddAccount?: () => void
}

export function SettingsModal({
  embedded = false,
  forceOpen = false,
  onClose,
  onCustomizeAccount,
  onAddAccount,
}: SettingsModalProps = {}) {
  const { t } = useTranslation()
  const open = useUiStore((s) => s.settingsOpen)
  const setSettingsOpen = useUiStore((s) => s.setSettingsOpen)
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const background = useUiStore((s) => s.background)
  const setBackground = useUiStore((s) => s.setBackground)
  const fontSizePx = useUiStore((s) => s.fontSizePx)
  const setFontSizePx = useUiStore((s) => s.setFontSizePx)
  const listDensity = useUiStore((s) => s.listDensity)
  const setListDensity = useUiStore((s) => s.setListDensity)
  const aiPrivacy = useUiStore((s) => s.aiPrivacy)
  const setAiPrivacy = useUiStore((s) => s.setAiPrivacy)
  const a11yHighContrast = useUiStore((s) => s.a11yHighContrast)
  const a11yReduceMotion = useUiStore((s) => s.a11yReduceMotion)
  const a11yComfort = useUiStore((s) => s.a11yComfort)
  const a11yVoiceConfirm = useUiStore((s) => s.a11yVoiceConfirm)
  const setA11y = useUiStore((s) => s.setA11y)
  const accountPrefs = useUiStore((s) => s.accountPrefs)
  const setAccountPref = useUiStore((s) => s.setAccountPref)
  const [openAcct, setOpenAcct] = useState<Record<string, boolean>>({})

  const visible = forceOpen || open
  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
    enabled: visible,
  })
  const accounts = accountsQ.data?.items || []

  if (!visible) return null

  const aiNote = AI_MODES.find((m) => m.id === aiPrivacy)?.noteKey || 'settings.aiLocalNote'

  function close() {
    setSettingsOpen(false)
    onClose?.()
  }

  const form = (
    <>
      <div className="mail-set-sec">
        <h4>{t('settings.appearance')}</h4>
        <div className="mail-chips">
          {THEMES.map((th) => (
            <button
              key={th.id}
              type="button"
              className={`mail-chip${theme === th.id ? ' on' : ''}`}
              onClick={() => setTheme(th.id)}
            >
              <Icon name={th.icon} size={16} /> {t(th.labelKey)}
            </button>
          ))}
        </div>
        <h4 style={{ marginTop: 14 }}>{t('settings.background')}</h4>
        <div className="mail-chips">
          {BGS.map((id) => (
            <button
              key={id}
              type="button"
              className={`mail-bgopt${background === id ? ' on' : ''}`}
              style={{ background: BG_PRESETS[id].swatch }}
              title={id}
              onClick={() => setBackground(id)}
              aria-label={id}
            />
          ))}
        </div>
        <p className="mail-set-note">{t('settings.bgNote')}</p>
      </div>

      <div className="mail-set-sec">
        <h4>{t('settings.text')}</h4>
        <div className="mail-slider-row">
          <span>A</span>
          <input
            type="range"
            min={13}
            max={20}
            value={fontSizePx}
            onChange={(e) => setFontSizePx(Number(e.target.value))}
            aria-label={t('settings.fontSize')}
          />
          <b style={{ fontSize: '1.2em' }}>A</b>
          <span>{fontSizePx}px</span>
        </div>
        <div className="mail-chips" style={{ marginTop: 8 }}>
          {DENSITIES.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`mail-chip${listDensity === d.id ? ' on' : ''}`}
              onClick={() => setListDensity(d.id)}
            >
              {t(d.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="mail-set-sec">
        <h4>{t('settings.accounts')}</h4>
        <p className="mail-set-note">{t('settings.accountsNote')}</p>
        {accounts.length === 0 ? (
          <p className="mail-set-note">{t('settings.noAccounts')}</p>
        ) : (
          <div className="mail-acct-prefs mail-acct-acc">
            {accounts.map((a, idx) => {
              const pref = accountPrefs[a.id] || {}
              const color = pref.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length]
              const isOpen = Boolean(openAcct[a.id])
              return (
                <div key={a.id} className={`mail-acct-acc-item${isOpen ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="mail-acct-acc-sum"
                    aria-expanded={isOpen}
                    onClick={() => setOpenAcct((m) => ({ ...m, [a.id]: !m[a.id] }))}
                  >
                    <span className="ava" style={{ background: color }}>
                      {(pref.initial || a.label || a.username || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <div className="meta">
                      <b>{a.label || a.username}</b>
                      <span>{a.username}</span>
                    </div>
                    <Icon name="expand_more" size={18} />
                  </button>
                  {isOpen ? (
                    <div className="mail-acct-pref-row">
                      <label>
                        {t('settings.accountColor')}
                        <input
                          type="color"
                          value={color.startsWith('#') ? color : '#6d28d9'}
                          onChange={(e) => setAccountPref(a.id, { color: e.target.value })}
                        />
                      </label>
                      <label>
                        {t('settings.accountInitial')}
                        <input
                          type="text"
                          maxLength={2}
                          value={pref.initial ?? ''}
                          placeholder={(a.label || a.username || '?').slice(0, 1)}
                          onChange={(e) => setAccountPref(a.id, { initial: e.target.value.slice(0, 2) })}
                        />
                      </label>
                      <label>
                        {t('settings.accountLabel')}
                        <input
                          type="text"
                          maxLength={32}
                          value={pref.label ?? ''}
                          placeholder={a.label || a.username}
                          onChange={(e) => setAccountPref(a.id, { label: e.target.value })}
                        />
                      </label>
                      {onCustomizeAccount ? (
                        <button
                          type="button"
                          className="mail-chip"
                          onClick={() => onCustomizeAccount(a.id)}
                        >
                          {t('mail.customizeAccount')}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}
        {onAddAccount ? (
          <button
            type="button"
            className="mail-acct-add"
            onClick={() => {
              setSettingsOpen(false)
              onAddAccount()
            }}
          >
            <Icon name="add" size={20} />
            <span>{t('settings.addAccount')}</span>
          </button>
        ) : null}
      </div>

      <div className="mail-set-sec">
        <h4>{t('settings.aiPrivacy')}</h4>
        <div className="mail-chips">
          {AI_MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`mail-chip${aiPrivacy === m.id ? ' on' : ''}`}
              onClick={() => setAiPrivacy(m.id)}
            >
              {t(m.labelKey)}
            </button>
          ))}
        </div>
        <p className="mail-set-note">{t(aiNote)}</p>
      </div>

      <div className="mail-set-sec">
        <h4>{t('settings.a11y')}</h4>
        <div className="mail-chips">
          <button
            type="button"
            className={`mail-chip${a11yHighContrast ? ' on' : ''}`}
            onClick={() => setA11y({ highContrast: !a11yHighContrast })}
          >
            {t('settings.a11yContrast')}
          </button>
          <button
            type="button"
            className={`mail-chip${a11yReduceMotion ? ' on' : ''}`}
            onClick={() => setA11y({ reduceMotion: !a11yReduceMotion })}
          >
            {t('settings.a11yMotion')}
          </button>
          <button
            type="button"
            className={`mail-chip${a11yComfort ? ' on' : ''}`}
            onClick={() => setA11y({ comfort: !a11yComfort })}
          >
            {t('settings.a11yComfort')}
          </button>
          <button
            type="button"
            className={`mail-chip${a11yVoiceConfirm ? ' on' : ''}`}
            onClick={() => setA11y({ voiceConfirm: !a11yVoiceConfirm })}
          >
            <Icon name="volume_up" size={16} /> {t('settings.a11yVoice')}
          </button>
        </div>
      </div>
    </>
  )

  if (embedded) {
    return <div className="mail-m-body rd-sec-body">{form}</div>
  }

  return (
    <div className="mail-ovl" role="dialog" aria-modal="true" aria-label={t('settings.title')}>
      <div className="mail-modal" style={{ width: 560 }}>
        <div className="mail-m-head">
          <b>{t('settings.title')}</b>
          <button type="button" className="mail-m-x" onClick={close} aria-label={t('auth.cancel')}>
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="mail-m-body">{form}</div>
      </div>
    </div>
  )
}
